import { z } from 'zod'
import type { Definitie } from './definitie.js'
import { Sha256 } from './document.js'
import { DefinitieId } from './id.js'
import { Moment, Person } from './moment.js'
import { Gap, GATE_CHECKS } from './hardened.js'

/**
 * De soorten waarschuwing van de publicatiepoort: advies dat publiceren niet tegenhoudt.
 *
 * Erbij sinds 30 oktober 2026, naar het model van het EU Funding & Tenders-portaal dat het ontwerp
 * van die dag volgt: fouten blokkeren, waarschuwingen niet. Geen eis vraagt om een waarschuwing bij
 * publiceren, en geen eis wordt er zwakker door: een gat blokkeert nog precies zoals voorheen (D-27,
 * D-46). Wat hier staat zijn twee dingen die de poort kan zien en die bijna altijd een vergissing
 * zijn, maar niet altijd:
 *
 * - `geen-limiet`: een lang tekstveld zonder teken- of woordlimiet. De limiet is per veld in te
 *   stellen (D-11) en de teller van de invuller telt hem af (R-12); zonder limiet telt er niets, en
 *   bij een subsidieaanvraag is dat zelden de bedoeling.
 * - `verplicht-intern`: een verplicht veld dat niet voor externen zichtbaar is (R-170, D-54). Een
 *   harde verplicht-regel houdt indienen tegen (R-26, R-81); staat het veld alleen intern, dan is de
 *   kans groot dat de aanvrager het niet kan of hoort in te vullen.
 * - `bron-na-doel`: een zichtbaarheidsregel waarvan de bron na het doel staat (besluit van 31 oktober
 *   2026, eisenboek 12.31, keuze 7). De invuller ziet het doel dan pas verschijnen als hij er al voorbij
 *   is. Er zijn redelijke gevallen, dus geen weigering. Een verslag van vóór die dag kent de soort niet
 *   en blijft geldig, want de lijst groeit alleen.
 */
export const GATE_WARNINGS = ['geen-limiet', 'verplicht-intern', 'bron-na-doel'] as const
export type GateWarningKind = (typeof GATE_WARNINGS)[number]

/** Eén waarschuwing, met het pad in de conceptstand zoals een gat dat heeft. */
export const GateWarning = z.strictObject({
  soort: z.enum(GATE_WARNINGS),
  pad: z.array(z.union([z.string(), z.number().int()])),
  wat: z.string().min(1),
})
export type GateWarning = z.infer<typeof GateWarning>

/**
 * Het bewijs dat de publicatiecontroles zijn gelopen (ontwerp 8.8 stap 1, D-46).
 *
 * **Waarom dit bestaat en niet een vlag.** De poort zit server-side in de publicatiestap
 * van de configurator, niet in de browser en niet in de store: de store kent de
 * definitie niet en kan de controles dus niet zelf uitvoeren (3.1). Wat de store wél
 * afdwingt is dat er geen publicatie ontstaat zonder het bewijs dat die stap is
 * doorlopen. Ontwerp 8.8 zegt het scherp: een knop die grijs wordt is geen poort.
 *
 * **De hash is wat het bewijs bindt aan een stand.** Zonder hash zou een verslag over
 * een oudere stand een nieuwere publicatie kunnen dekken: controleren, dan nog iets
 * wijzigen, dan publiceren. De hash gaat over de canonieke serialisatie van de
 * definitie die bevriest; wijkt hij af van wat de store aangeboden krijgt, dan telt het
 * verslag niet.
 *
 * **Elke controle staat erin, ook als hij niets vond.** Een verslag met alleen
 * bevindingen zou niet laten zien wat er is gekeken, en dan is later niet vast te
 * stellen of een controle heeft gelopen of ontbrak. Dat is precies het onderscheid dat
 * een audit nodig heeft.
 */
export const CheckReport = z
  .strictObject({
    /** De definitie waarover de controles liepen. */
    definitieId: DefinitieId,
    /**
     * De hash van de canonieke serialisatie van die stand.
     *
     * Canoniek en niet zoals het toevallig op de lijn stond: `JSON.stringify` volgt de
     * invoegorde van de sleutels, en dan zou de hash afhangen van de vorm van een
     * objectliteral in plaats van van de inhoud van de definitie.
     */
    definitiehash: Sha256,
    /** Wie de controles heeft laten lopen, uit het token en nooit uit het lichaam. */
    persoon: Person,
    moment: Moment,
    /**
     * Per controle wat er is gevonden, in de volgorde van `GATE_CHECKS`. Een lege
     * lijst betekent: gelopen en niets gevonden.
     */
    uitkomsten: z.array(
      z.strictObject({
        controle: z.enum(GATE_CHECKS),
        gaten: z.array(Gap),
      }),
    ),
    /**
     * Wat de poort aanraadt zonder te blokkeren (`GATE_WARNINGS`). Optioneel, zodat een verslag van
     * vóór 30 oktober 2026 zonder deze sleutel geldig blijft; een leeg of ontbrekend veld betekent
     * hetzelfde: geen waarschuwingen. Telt niet mee in `blocks`.
     */
    waarschuwingen: z.array(GateWarning).optional(),
  })
  .superRefine((report, ctx) => {
    const listed = report.uitkomsten.map((outcome) => outcome.controle)
    if (listed.length !== GATE_CHECKS.length || listed.some((name, i) => name !== GATE_CHECKS[i])) {
      ctx.addIssue({
        code: 'custom',
        path: ['uitkomsten'],
        // Een verslag dat een controle niet noemt is niet te onderscheiden van een
        // verslag waarin die controle niets vond, en dan bewijst het niets.
        message: `verwacht alle controles in de vaste volgorde ${GATE_CHECKS.join(', ')}`,
      })
    }
    report.uitkomsten.forEach((outcome, i) => {
      outcome.gaten.forEach((gap, g) => {
        if (gap.controle !== outcome.controle) {
          ctx.addIssue({
            code: 'custom',
            path: ['uitkomsten', i, 'gaten', g, 'controle'],
            message: `dit gat hoort bij ${gap.controle} en staat onder ${outcome.controle}`,
          })
        }
      })
    })
  })

export type CheckReport = z.infer<typeof CheckReport>

/** Blokkeert dit verslag het publiceren? Elk gat blokkeert; een waarschuwing nooit (D-46). */
export function blocks(report: CheckReport): boolean {
  return report.uitkomsten.some((outcome) => outcome.gaten.length > 0)
}

/**
 * De canonieke serialisatie van een definitie: sleutels op alfabetische volgorde.
 *
 * Dit is wat `definitiehash` dekt. `JSON.stringify` volgt de invoegorde van de
 * sleutels, dus zonder deze stap zou de hash afhangen van de vorm van een objectliteral
 * in plaats van van de inhoud van de definitie: dezelfde definitie langs twee wegen
 * opgebouwd zou twee hashes geven, en dan bewijst het verslag niets.
 *
 * **Hij staat hier en niet in de engine, en dat is een correctie.** In M5.4 stond hij
 * bij de publicatiecontroles, en dat leek logisch omdat de poort hem gebruikt. Bij
 * M5.6 bleek het niet te houden: de store moet de hash narekenen voordat zij een
 * publicatie aanneemt (ontwerp 8.8), en de store mag geen formulierlogica bevatten
 * (3.1) en dus niet van de engine afhangen. Een canonieke serialisatie is bovendien
 * geen logica over een definitie maar een eigenschap van haar vorm, en die hoort bij
 * het model.
 */
export function canonical(definitie: Definitie): string {
  return JSON.stringify(sort(definitie))
}

function sort(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sort)
  if (value === null || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(value).sort()) {
    out[key] = sort((value as Record<string, unknown>)[key])
  }
  return out
}

/**
 * De hash van een definitie, zoals hij in het controleverslag staat.
 *
 * Via `crypto.subtle` en dus asynchroon, want dat is de enige SHA-256 die zowel in Node
 * als in de browser bestaat. Dat is hier nodig omdat drie kanten hem uitrekenen: de
 * browser om te tonen wat er nog mist, de publicatiestap om het verslag te schrijven,
 * en de store om te controleren dat het verslag over déze definitie gaat. `id.ts`
 * gebruikt `globalThis.crypto` al voor dezelfde reden.
 */
export async function definitieHash(definitie: Definitie): Promise<Sha256> {
  const bytes = new TextEncoder().encode(canonical(definitie))
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
  return Sha256.parse(`sha256:${hex}`)
}
